/**
 * Platform-agnostic Y.Doc persistence factory.
 *
 * The sync engine's Yjs layer needs to persist `Y.Doc` state locally so
 * documents survive page reload / app restart. On web, this is
 * `IndexeddbPersistence` from `y-indexeddb`. React Native cannot use
 * `y-indexeddb` (no IndexedDB) — it ships its own adapter (e.g. SQLite-
 * backed, MMKV-backed, or a no-op that relies on server replay).
 *
 * Callers create persistence through `createYjsPersistence(name, doc)`.
 * Web registers the y-indexeddb factory once at startup; RN registers
 * its own. The interface is the minimal subset of `y-indexeddb`'s public
 * API actually exercised by the sync engine.
 */

import type * as Y from 'yjs';

export type YjsPersistenceEvent = 'synced';

/**
 * Platform-neutral Yjs persistence handle. The shape mirrors
 * `IndexeddbPersistence` from `y-indexeddb` but only the methods the sync
 * engine actually calls — keep this surface tight so RN adapters do less
 * work to satisfy it.
 */
export interface YjsPersistence {
  on(event: YjsPersistenceEvent, listener: () => void): void;
  off(event: YjsPersistenceEvent, listener: () => void): void;
  /** Close the connection. Data on disk is preserved. */
  destroy(): Promise<void> | void;
  /** Wipe the persisted store entirely. Used when a Y.Doc is deleted. */
  clearData(): Promise<void> | void;
}

export type YjsPersistenceFactory = (storeName: string, ydoc: Y.Doc) => YjsPersistence;

let _factory: YjsPersistenceFactory | null = null;

/**
 * Register the platform-specific Yjs persistence factory. Must be called
 * before any `Y.Doc` provider mounts (typically in your app entry point).
 *
 * @example
 *   // Web
 *   import { IndexeddbPersistence } from "y-indexeddb";
 *   setYjsPersistenceFactory((name, doc) => new IndexeddbPersistence(name, doc));
 *
 *   // RN — no local persistence (server replay only)
 *   setYjsPersistenceFactory(() => noopYjsPersistence());
 */
export function setYjsPersistenceFactory(factory: YjsPersistenceFactory | null): void {
  _factory = factory;
}

/**
 * Create a Y.Doc persistence handle via the registered factory.
 * Falls back to a no-op (with a console warning) if no factory was
 * registered, so the engine doesn't hard-crash in tests / setups that
 * don't need local persistence.
 */
export function createYjsPersistence(storeName: string, ydoc: Y.Doc): YjsPersistence {
  if (!_factory) {
    if (typeof console !== 'undefined') {
      console.warn(
        '[YjsPersistence] No factory registered — falling back to no-op. ' +
          'Call setYjsPersistenceFactory() in your platform entry point.',
      );
    }
    return noopYjsPersistence();
  }
  return _factory(storeName, ydoc);
}

/** Persistence adapter that does nothing — useful for RN cold-start or tests. */
export function noopYjsPersistence(): YjsPersistence {
  return {
    on(_event, listener) {
      // Fire 'synced' once on the next microtask so callers that wait for
      // it don't hang. No actual data was loaded.
      if (_event === 'synced') {
        Promise.resolve().then(() => listener());
      }
    },
    off() {},
    destroy() {},
    clearData() {},
  };
}
